import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../i18n/LanguageContext";
import { IMAGES } from "../data/vessels";

export const Manifesto = () => {
  const { t } = useLanguage();
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="philosophy" className="relative py-24 sm:py-36 bg-[#050B14] overflow-hidden" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <motion.div
          ref={imgRef}
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:sticky lg:top-32"
        >
          <div className="relative overflow-hidden border border-[rgba(212,175,55,0.2)] aspect-[4/5]">
            <motion.img
              src={IMAGES.interior}
              alt={t.manifesto.imageCaption}
              loading="lazy"
              style={{ y: imgY, scale: 1.18 }}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050B14]/60 to-transparent" />
          </div>
          <p className="mt-4 font-label text-[10px] tracking-[0.35em] uppercase text-slate-500" data-testid="manifesto-image-caption">
            {t.manifesto.imageCaption}
          </p>
          <div className="absolute -top-5 -left-5 w-24 h-24 border-t border-l border-[#D4AF37]/40 pointer-events-none" />
          <div className="absolute -bottom-5 -right-5 w-24 h-24 border-b border-r border-[#D4AF37]/40 pointer-events-none" />
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-label text-[10px] sm:text-xs tracking-[0.45em] uppercase text-[#D4AF37] mb-5" data-testid="manifesto-overline">
              {t.manifesto.overline}
            </p>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F7F5F0] leading-[1.05] mb-14" data-testid="manifesto-title">
              {t.manifesto.title}
            </h2>
          </motion.div>

          {t.manifesto.chapters.map((ch, i) => (
            <motion.div
              key={ch.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="border-t border-[rgba(212,175,55,0.18)] py-10 flex gap-8"
              data-testid={`manifesto-chapter-${ch.num}`}
            >
              <span className="font-display text-5xl sm:text-6xl font-light text-outline-gold leading-none shrink-0">{ch.num}</span>
              <div>
                <h3 className="font-display text-2xl sm:text-3xl text-[#F7F5F0] font-light mb-4">{ch.title}</h3>
                <p className="text-sm sm:text-base text-slate-400 font-light leading-relaxed">{ch.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
